import { BaseMutationOptions, useApolloClient } from "@apollo/client/react"
import { useTranslation } from "react-i18next"

import type { ApolloError, QueryOptions } from "@apollo/client/core"

import { callMutation } from "shared/api/callMutation"
import { evict } from "shared/api/evict"
import { useNeoletterUserProfile } from "shared/api/neoletter-user-profile/useNeoletterUserProfile"
import { evictProjectPrice } from "shared/api/price/evictPriceCache"
import { MutationTypename } from "shared/api/types"
import { Logger } from "shared/lib/Logger"
import { emitAppEvent } from "shared/lib/appEventEmitter"
import { generateUUID } from "shared/lib/generateUUID"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import {
  NEW_PROJECT_COLOR,
  Project,
  ProjectPhaseTypename,
  ProjectTypename,
  ProjectUITypename
} from "shared/types/Project"
import { CREATE_PROJECT } from "shared/types/ProjectMutations"
import { InvalidDataError } from "shared/types/error/InvalidDataError"

import { useInflationSurchargeQuery } from "entities/projects/graphql/inflation-surcharge/queries.generated"
import {
  CreateProjectMutation,
  CreateProjectMutationVariables,
  useCreateProjectMutation
} from "entities/projects/graphql/mutations.generated"
import { usePriceListsQuery } from "entities/projects/graphql/price-lists/queries.generated"
import { evictProjectHistory } from "entities/projects/graphql/project-history/evictProjectHistory"
import {
  useProjectChildrenLazyQuery,
  useProjectsLazyQuery
} from "entities/projects/graphql/queries.generated"
import { CreateProjectTransformSchema } from "entities/projects/lib/CreateProjectTransformSchema"
import { createProjectInCache } from "entities/projects/lib/createProjectInCache"
import { useProjectsActions } from "entities/projects/model/store/useProjectsActions"
import { z } from "zod"

import {
  AllProjectPermissionsDocument,
  ProjectPermissionsDocument
} from "../graphql/queries.generated"

const StrictCreateProjectSchema = z.object({
  id: z.string(),
  title: z.string(),
  phase: z.object({
    id: z.string(),
    label: z.string(),
    value: z.string(),
    __typename: z
      .literal(ProjectPhaseTypename)
      .nullish()
      .transform(() => ProjectPhaseTypename)
  }),
  ui: z.object({
    color: z.string(),
    __typename: z
      .literal(ProjectUITypename)
      .nullish()
      .transform(() => ProjectUITypename)
  })
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- only referenced through `typeof` below, which typescript-eslint 8 counts as unused
const CreateableProjectSchema = StrictCreateProjectSchema.extend({
  id: StrictCreateProjectSchema.shape.id.optional(),
  ui: StrictCreateProjectSchema.shape.ui
    .extend({
      color: z.string().nullable().optional()
    })
    .optional()
    .nullable()
})

export type CreateableProject = z.infer<typeof CreateableProjectSchema>

export function useCreateProject() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const { data: priceListData } = usePriceListsQuery()
  const { data: inflationSurchargeData } = useInflationSurchargeQuery()
  const { setProjectPending, unsetProjectPending } = useProjectsActions()
  const { updateProfile } = useNeoletterUserProfile()
  const startProjectRequest = useProjectRequestMonitor()
  const [fetchProjects] = useProjectsLazyQuery()
  const [fetchProject] = useProjectChildrenLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Project.CREATE_PROJECT.Start"),
      success: t("Request.Project.CREATE_PROJECT.Success"),
      failure: t("Request.Project.CREATE_PROJECT.Failure")
    }
  })

  const handleError = useEvent(
    (
      error: ApolloError,
      options:
        | BaseMutationOptions<
            CreateProjectMutation,
            Partial<CreateProjectMutationVariables>
          >
        | undefined
    ) => {
      if (options?.variables?.input?.id) {
        evict({
          id: options?.variables?.input?.id,
          typename: ProjectTypename,
          cache: apolloCache
        })
      }
    }
  )

  const [mutate, rest] = useCreateProjectMutation({
    onError: handleError,
    update: (cache, result) => {
      createProjectInCache({
        cache,
        project: result.data?.project
          ? {
              ...result.data.project,
              team: null
            }
          : null,
        writeChildrenQuery: true
      })
    },
    refetchQueries: result => {
      const refetchQueries: QueryOptions[] = [
        {
          query: AllProjectPermissionsDocument
        }
      ]
      if (result?.data?.project.id) {
        refetchQueries.push({
          query: ProjectPermissionsDocument,
          variables: { projectID: result?.data?.project.id }
        })
      }
      return refetchQueries
    }
  })

  const createProject = useEvent((project: CreateableProject) => {
    const { data: input, error } = StrictCreateProjectSchema.safeParse({
      id: generateUUID(),
      ui: { color: NEW_PROJECT_COLOR },
      ...project
    })

    if (!input || error) {
      const resolvedError = new InvalidDataError("Invalid Project Input", {
        data: {
          project,
          error
        }
      })
      Logger.error(resolvedError)
      throw resolvedError
    }

    const { data: transformedProject } = CreateProjectTransformSchema.safeParse(
      {
        ...input,
        __typename: ProjectTypename
      }
    )

    const optimisticProject = {
      ...(transformedProject ?? input),
      phase: input.phase ?? null,
      ui: input.ui ?? null,
      position: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      priceList:
        priceListData?.priceLists?.find(pl => pl.isCurrent)?.id ?? null,
      inflationSurcharge:
        inflationSurchargeData?.inflationSurcharge.value ?? null,
      __typename: ProjectTypename
    }
    const optimisticResponse = {
      __typename: MutationTypename,
      project: optimisticProject
    }

    const { id } = optimisticProject
    const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
    const completeProjectRequest = startProjectRequest({
      projectID: id,
      requestType: CREATE_PROJECT
    })

    emitAppEvent(CREATE_PROJECT, { id })
    setProjectPending({ projectID: id })
    updateProfile({ customFields: { active_project_id: id } })

    return callMutation<
      CreateProjectMutation,
      CreateProjectMutationVariables,
      Project
    >(mutate, {
      variables: { input },
      transform: data => data.project,
      apolloOptions: {
        optimisticResponse,
        onCompleted: () => {
          completeProjectRequest()
          unsetProjectPending({ projectID: id })
          fetchProject({ variables: { id } })
          evictProjectHistory(id, apolloCache)
          raiseSuccessToast()
        },
        onError: () => {
          completeProjectRequest()
          fetchProject({ variables: { id } })
          evictProjectHistory(id, apolloCache)
          evictProjectPrice(id, apolloCache, true)
          fetchProjects()
          raiseErrorToast()
        }
      }
    })
  })
  return {
    createProject,
    ...rest
  }
}
